import React, { useState } from 'react';
import './TestMode.css';
import { getAllAIPlayers } from '../config/ai-players-config';
import { getCompatiblePatterns } from '../config/patterns';
import { calculateAIMove, getPlayerId, STRATEGIES } from '../config/oracle-ai';
import { checkWin } from '../config/patterns';

const TestMode = ({ onBackToGame }) => {
  const [testConfig, setTestConfig] = useState({
    player1Id: 'prof-corey',
    player2Id: 'erdos',
    strategy: STRATEGIES.AI,
    gridSize: 6,
    winningPattern: 'tic',
    numberOfGames: 100,
    fastMode: true
  });
  
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [currentGame, setCurrentGame] = useState(0);

  const aiPlayers = getAllAIPlayers();
  const patterns = getCompatiblePatterns(testConfig.gridSize);

  const handleConfigChange = (field, value) => {
    setTestConfig(prev => {
      const newConfig = {
        ...prev,
        [field]: value
      };
      
      // If grid size changed, update target pattern to be compatible
      if (field === 'gridSize') {
        const compatiblePatterns = getCompatiblePatterns(value);
        const currentPattern = newConfig.winningPattern;
        
        // If current pattern is not compatible, switch to first compatible pattern
        if (!compatiblePatterns.some(p => p.id === currentPattern)) {
          newConfig.winningPattern = compatiblePatterns[0]?.id || 'tic';
        }
      }
      
      return newConfig;
    });
  };

  const initializeBoard = (size) => {
    return Array(size).fill(null).map(() => Array(size).fill(null));
  };

  const checkDraw = (board) => {
    return board.every(row => row.every(cell => cell !== null));
  };

  const runSingleGame = async (board, pattern, player1Id, player2Id, strategy, boardSize) => {
    let currentBoard = board.map(row => [...row]);
    let currentPlayer = 'X';
    let gameState = 'playing';

    while (gameState === 'playing') {
      // Determine AI player ID for current player
      const playerId = currentPlayer === 'X' ? getPlayerId(player1Id) : getPlayerId(player2Id);
      
      // Calculate AI move
      const aiMove = await calculateAIMove(currentBoard, pattern, playerId, boardSize, strategy, false);
      
      if (!aiMove) {
        // No valid moves left - it's a draw
        return 'draw';
      }

      // Make the move
      currentBoard[aiMove.row][aiMove.col] = currentPlayer;

      // Check for win
      const winResult = checkWin(currentBoard, pattern, currentPlayer, boardSize);
      if (winResult) {
        return currentPlayer === 'X' ? 'player1' : 'player2';
      }

      // Check for draw
      if (checkDraw(currentBoard)) {
        return 'draw';
      }

      // Switch players
      currentPlayer = currentPlayer === 'X' ? 'O' : 'X';
    }
  };

  const runTest = async () => {
    setIsRunning(true);
    setResults(null);
    setCurrentGame(0);

    const stats = {
      player1Wins: 0,
      player2Wins: 0,
      draws: 0,
      totalGames: testConfig.numberOfGames
    };

    for (let i = 0; i < testConfig.numberOfGames; i++) {
      // Only update progress if not in fast mode
      if (!testConfig.fastMode && (i % 10 === 0 || i === testConfig.numberOfGames - 1)) {
        setCurrentGame(i + 1);
      }
      
      const board = initializeBoard(testConfig.gridSize);
      const result = await runSingleGame(
        board, 
        testConfig.winningPattern, 
        testConfig.player1Id, 
        testConfig.player2Id, 
        testConfig.strategy,
        testConfig.gridSize
      );

      if (result === 'player1') {
        stats.player1Wins++;
      } else if (result === 'player2') {
        stats.player2Wins++;
      } else {
        stats.draws++;
      }

      // Minimal delay only if not in fast mode
      if (!testConfig.fastMode && i % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 1));
      }
    }

    setResults(stats);
    setIsRunning(false);
    setCurrentGame(0);
  };

  const getPlayerName = (playerId) => {
    const player = aiPlayers.find(p => p.id === playerId);
    return player ? player.name : playerId;
  };

  return (
    <div className="test-mode">
      <div className="test-header">
        <h2>AI Test Mode</h2>
        <button onClick={onBackToGame} className="back-button">
          ← Back to Game
        </button>
      </div>

      <div className="test-container">
        <div className="test-config">
          <h3>Test Configuration</h3>
          
          <div className="config-grid">
            <div className="config-item">
              <label>Player 1:</label>
              <select 
                value={testConfig.player1Id}
                onChange={(e) => handleConfigChange('player1Id', e.target.value)}
                disabled={isRunning}
              >
                {aiPlayers.map(player => (
                  <option key={player.id} value={player.id}>
                    {player.name} ({player.level})
                  </option>
                ))}
              </select>
            </div>

            <div className="config-item">
              <label>Player 2:</label>
              <select 
                value={testConfig.player2Id}
                onChange={(e) => handleConfigChange('player2Id', e.target.value)}
                disabled={isRunning}
              >
                {aiPlayers.map(player => (
                  <option key={player.id} value={player.id}>
                    {player.name} ({player.level})
                  </option>
                ))}
              </select>
            </div>

            <div className="config-item">
              <label>Strategy:</label>
              <select 
                value={testConfig.strategy}
                onChange={(e) => handleConfigChange('strategy', e.target.value)}
                disabled={isRunning}
              >
                <option value={STRATEGIES.AI}>AI (Random)</option>
                <option value={STRATEGIES.RANDOM}>Random</option>
                <option value={STRATEGIES.GARDNER}>Gardner (Defensive)</option>
              </select>
            </div>

            <div className="config-item">
              <label>Grid Size:</label>
              <select 
                value={testConfig.gridSize}
                onChange={(e) => handleConfigChange('gridSize', parseInt(e.target.value))}
                disabled={isRunning}
              >
                <option value={3}>3x3</option>
                <option value={4}>4x4</option>
                <option value={5}>5x5</option>
                <option value={6}>6x6</option>
                <option value={7}>7x7</option>
                <option value={8}>8x8</option>
              </select>
            </div>

            <div className="config-item">
              <label>Target Pattern:</label>
              <select 
                value={testConfig.winningPattern}
                onChange={(e) => handleConfigChange('winningPattern', e.target.value)}
                disabled={isRunning}
              >
                {patterns.map(pattern => (
                  <option key={pattern.id} value={pattern.id}>
                    {pattern.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="config-item">
              <label>Number of Games:</label>
              <input 
                type="number"
                min="1"
                max="1000"
                value={testConfig.numberOfGames}
                onChange={(e) => handleConfigChange('numberOfGames', parseInt(e.target.value))}
                disabled={isRunning}
              />
            </div>

            <div className="config-item">
              <label>
                <input 
                  type="checkbox"
                  checked={testConfig.fastMode}
                  onChange={(e) => handleConfigChange('fastMode', e.target.checked)}
                  disabled={isRunning}
                />
                Fast Mode (no progress updates)
              </label>
            </div>
          </div>

          <button 
            onClick={runTest} 
            className="start-test-button"
            disabled={isRunning}
          >
            {isRunning ? 
              (testConfig.fastMode ? 
                `Running ${testConfig.numberOfGames} games...` : 
                `Running Game ${currentGame}/${testConfig.numberOfGames}...`
              ) : 
              'Start Test'
            }
          </button>
        </div>

        {results && (
          <div className="test-results">
            <h3>Test Results</h3>
            <div className="results-summary">
              <div className="result-item">
                <span className="result-label">{getPlayerName(testConfig.player1Id)} Wins:</span>
                <span className="result-value">{results.player1Wins}</span>
                <span className="result-percentage">
                  ({((results.player1Wins / results.totalGames) * 100).toFixed(1)}%)
                </span>
              </div>
              
              <div className="result-item">
                <span className="result-label">{getPlayerName(testConfig.player2Id)} Wins:</span>
                <span className="result-value">{results.player2Wins}</span>
                <span className="result-percentage">
                  ({((results.player2Wins / results.totalGames) * 100).toFixed(1)}%)
                </span>
              </div>
              
              <div className="result-item">
                <span className="result-label">Draws:</span>
                <span className="result-value">{results.draws}</span>
                <span className="result-percentage">
                  ({((results.draws / results.totalGames) * 100).toFixed(1)}%)
                </span>
              </div>
              
              <div className="result-item total">
                <span className="result-label">Total Games:</span>
                <span className="result-value">{results.totalGames}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestMode;
